<template>
    <div class="flex flex-col h-full px-4 pt-2 mx-auto">
        <h1 class="flex-shrink-0 px-2 py-2 text-xl font-bold text-gray-900">{{ $t("deviceAlarmsNotificationSubscription.listPageTitle") }}</h1>

        <div class="flex justify-center flex-grow pt-4 md:pt-8">

            <div v-if="isLoading" class="flex items-center justify-center w-full">
                <div class="flex items-center justify-center w-full h-40">
                    <LoadingSpinner />
                </div>
            </div>

            <div v-else class="w-full max-w-3xl py-6 space-y-12">

                <div v-if="currentTenants.length > 0">
                    <Combobox as="div" v-model="selectedTenant">
                        <label class="block text-sm font-medium text-gray-700">
                            {{ $t("deviceAlarmsNotificationSubscription.selectTenant") }}
                        </label>
                        <div class="relative mt-2">
                            <ComboboxInput class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" :display-value="(tenant) => tenant?.name" @change="tenantQuery = $event.target.value" />
                            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none">
                                <ChevronDownIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                            </ComboboxButton>

                            <ComboboxOptions class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                <ComboboxOption v-if="filteredTenants.length === 0 && tenantQuery !== ''" class="relative px-4 py-2 text-gray-700 cursor-default select-none">
                                    {{ $t("common.noResults") }}
                                </ComboboxOption>

                                <ComboboxOption v-for="tenant in filteredTenants" :key="tenant.id" :value="tenant" as="template" v-slot="{ selected, active }">
                                    <li :class="[
                                        'relative cursor-default select-none py-2 pl-3 pr-9',
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                                    ]">
                                        <span :class="['block truncate', selected && 'font-semibold']">
                                            {{ tenant.name }}
                                        </span>

                                        <span v-if="selected" :class="[
                                            'absolute inset-y-0 right-0 flex items-center pr-4',
                                            active ? 'text-white' : 'text-indigo-600',
                                        ]">
                                            <CheckIcon class="w-5 h-5" aria-hidden="true" />
                                        </span>
                                    </li>
                                </ComboboxOption>
                            </ComboboxOptions>
                        </div>
                    </Combobox>
                </div>
                <div v-else>
                    <p class="text-center text-gray-500">{{ $t("deviceAlarmsNotificationSubscription.selectTenant") }}</p>
                </div>

                <div v-if="selectedTenantId" class="space-y-8">
                    <div v-if="loadingDevices" class="flex items-center justify-center h-40">
                        <LoadingSpinner />
                    </div>

                    <template v-else>
                    <div>
                        <label for="email-list" class="block text-sm font-medium text-gray-700">{{ $t("deviceAlarmsNotificationSubscription.emailList") }}</label>
                        <div class="mt-1">
                            <div class="flex flex-wrap items-center gap-2 p-2 bg-white border border-gray-300 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
                                <span v-for="(email, index) in currentState.emails" :key="index" class="flex items-center gap-1 px-2 py-1 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-full">
                                    {{ email }}
                                    <button :disabled="(!isAdminForCurrentTenant && !isSuperAdmin)" @click="removeEmail(index)" class="text-indigo-500 rounded-full hover:text-indigo-800 focus:outline-none">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                                        </svg>
                                    </button>
                                </span>
                                <div class="flex items-center flex-grow">
                                    <input type="text" v-model="newEmail" :disabled="(!isAdminForCurrentTenant && !isSuperAdmin)" @keydown.enter.prevent="addEmail" @keydown.backspace="handleBackspace" @keyup="handleEmailKeyup" @blur="addEmail" @paste.prevent="handlePaste" :placeholder="$t('deviceAlarmsNotificationSubscription.emailListInputFieldText')" class="flex-grow p-1 text-sm bg-transparent border-0 focus:ring-0 min-w-[150px]" />
                                    <button v-if="newEmail.trim()" @click="addEmail" type="button" class="p-1 text-indigo-600 hover:text-indigo-800 focus:outline-none">
                                        <PlusIcon class="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <p class="mt-2 text-xs text-gray-500">{{ $t("deviceAlarmsNotificationSubscription.emailListDescription") }}</p>
                    </div>

                    <div>
                        <div class="flex items-center gap-1.5">
                            <label for="device-list" class="block text-sm font-medium text-gray-700">{{ $t("deviceAlarmsNotificationSubscription.excludedDevice") }}</label>
                            <div class="relative flex items-center group">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gray-400 cursor-pointer" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                                <div class="absolute z-10 invisible mb-3 transition-opacity duration-300 -translate-x-1/2 rounded-lg shadow-lg opacity-0 bottom-full left-1/2 w-max group-hover:opacity-100 group-hover:visible">
                                    <img src="@/assets/serial_number_example.png" alt="Beispiel für Seriennummer" class="rounded-lg max-w-none w-[825px]">
                                </div>
                            </div>
                        </div>
                        <div class="relative mt-1">
                            <div class="flex flex-wrap items-center gap-2 p-2 bg-white border border-gray-300 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-indigo-500">
                                <span v-for="(device, index) in currentState.excludedDevices" :key="index" class="flex items-center gap-1 px-2 py-1 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-full">
                                    {{ device }}
                                    <button @click="removeDevice(index)" :disabled="(!isAdminForCurrentTenant && !isSuperAdmin)" class="text-indigo-500 rounded-full hover:text-indigo-800 focus:outline-none">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                                        </svg>
                                    </button>
                                </span>
                                <input type="text" v-model="newDevice" :disabled="(!isAdminForCurrentTenant && !isSuperAdmin)" @input="handleDeviceInput" @keydown.enter.prevent="addDevice" @keydown.backspace="handleDeviceBackspace" @paste.prevent="handleDevicePaste" @focus="deviceInputFocused = true" @blur="handleDeviceBlur" :placeholder="$t('deviceAlarmsNotificationSubscription.excludedDeviceInputField')" class="flex-grow p-1 text-sm bg-transparent border-0 focus:ring-0" />
                            </div>

                            <div v-if="deviceInputFocused && (filteredDevices.length > 0 || loadingDevices)" class="absolute z-20 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg max-h-60">
                                <div v-if="loadingDevices && filteredDevices.length === 0" class="px-4 py-2 text-sm text-gray-500">
                                    {{ $t("deviceAlarmsNotificationSubscription.searchingDevices") }}
                                </div>
                                <button v-for="device in filteredDevices" :key="device.serialNumber" :disabled="(!isAdminForCurrentTenant && !isSuperAdmin)" @mousedown.prevent="selectDevice(device.serialNumber)" class="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-indigo-50 focus:outline-none focus:bg-indigo-50">
                                    <span class="font-medium" v-html="highlightMatch(device.serialNumber, newDevice)"></span>
                                </button>
                            </div>
                        </div>
                        <p v-if="deviceError" class="mt-2 text-xs text-red-600">{{ deviceError }}</p>
                        <p class="mt-2 text-xs text-gray-500">{{ $t("deviceAlarmsNotificationSubscription.excludedDeviceDescription") }}</p>
                    </div>

                    <div>
                        <label for="status-toggle" class="block text-sm font-medium text-gray-700">{{ $t("deviceAlarmsNotificationSubscription.subscriptionStatusSlider") }}</label>
                        <button type="button" @click="currentState.isActive = !currentState.isActive" :disabled="(!isAdminForCurrentTenant && !isSuperAdmin)" :class="[currentState.isActive ? 'bg-indigo-600' : 'bg-gray-200']" class="relative inline-flex flex-shrink-0 h-6 mt-1 transition-colors duration-200 ease-in-out border-2 border-transparent rounded-full cursor-pointer w-11 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" role="switch" :aria-checked="currentState.isActive">
                            <span :class="[currentState.isActive ? 'translate-x-5' : 'translate-x-0']" class="inline-block w-5 h-5 transition duration-200 ease-in-out transform bg-white rounded-full shadow-lg pointer-events-none ring-0"></span>
                        </button>
                    </div>

                    <div class="flex justify-end flex-shrink-0 py-4 mt-5 gap-x-4">
                        <button v-if="(isAdminForCurrentTenant || isSuperAdmin)" @click="handleAbort" :disabled="isAbortDisabled" class="px-4 py-2 ml-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ $t("common.action.cancel") }}
                        </button>
                        <button v-if="(isAdminForCurrentTenant || isSuperAdmin)" @click="handleDelete" :disabled="!subscriptionExists" class="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ $t("common.action.delete") }}
                        </button>
                        <button v-if="(isAdminForCurrentTenant || isSuperAdmin) && subscriptionExists" @click="handleUpdate" :disabled="isSaveDisabled" class="inline-flex justify-center px-4 py-2 ml-4 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                            {{ $t("common.action.apply") }}
                        </button>
                        <button v-if="(isAdminForCurrentTenant || isSuperAdmin) && !subscriptionExists" @click="handleCreate" :disabled="isCreateDisabled" class="inline-flex justify-center px-4 py-2 ml-4 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                            {{ $t("common.action.save") }}
                        </button>
                    </div>
                    </template>
                </div>
            </div>
        </div>
        <SimpleAlert @cancel="closeModal" @approve="handleConfirmAction" :open="showConfirmationModal" :title="modalTitle" :description="modalDescription" :action="modalActionText" :type="modalType" />
    </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { querySubscription, createSubscription, updateSubscription, deleteSubscription } from "@/api/subscription";
import { useStore } from "vuex";
import LoadingSpinner from "@/components/LoadingSpinner";
import SimpleAlert from "@/components/modals/SimpleAlert";
import { hasRightsForChosenTenant, USER_PERMISSIONS } from "@/lib/userHasPermission";
import { useI18n } from 'vue-i18n';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/vue";
import { CheckIcon, ChevronDownIcon, PlusIcon } from "@heroicons/vue/outline";

export default {
    name: "EmailNotifications",
    components: {
        LoadingSpinner,
        SimpleAlert,
        Combobox,
        ComboboxButton,
        ComboboxInput,
        ComboboxOption,
        ComboboxOptions,
        CheckIcon,
        ChevronDownIcon,
        PlusIcon
    },

    setup() {
        const { t } = useI18n();
        const store = useStore();
        const isLoading = ref(true);
        const isSubscriptionLoading = ref(false);
        const subscriptionExists = ref(false);
        const currentState = reactive({
            emails: [],
            excludedDevices: [],
            isActive: false,
        });
        const selectedTenantId = ref(null);
        const selectedTenant = ref(null);
        const tenantQuery = ref('');
        const isSuperAdmin = computed(() => { return store.getters["auth/isSuperAdmin"]; });

        const originalState = ref(null);
        const newEmail = ref('');
        const newDevice = ref('');
        const deviceInputFocused = ref(false);
        const loadingDevices = ref(false);
        const deviceError = ref('');

        const showConfirmationModal = ref(false);
        const modalTitle = ref('');
        const modalDescription = ref('');
        const modalActionText = ref('');
        const modalActionType = ref('');
        const modalType = ref('info');

        const allUsers = computed(() => store.state.users.all);
        const authUser = computed(() => store.state.auth.user);
        const allTenants = computed(() => store.state.allTenants);

        const tenantDevices = computed(() => {
            const tenants = store.state.tenants || [];
            const tenant = tenants.find(t => t.id === selectedTenantId.value);
            return tenant?.devices || [];
        });

        const isAbortDisabled = computed(() => {
            if (!originalState.value) return true;
            return JSON.stringify(currentState) === JSON.stringify(originalState.value);
        });

        const isSaveDisabled = computed(() => {
            if (!originalState.value) return true;
            return JSON.stringify(currentState) === JSON.stringify(originalState.value);
        });
        const isCreateDisabled = computed(() => {
            return (currentState.emails.length === 0)
        });

        const currentUser = computed(() => {
            if (!authUser.value || !allUsers.value || allUsers.value.size === 0) return null;
            const userKey = [...allUsers.value.keys()].find(k => k.includes(authUser.value.username));
            return allUsers.value.get(userKey);
        });

        const currentTenants = computed(() => {
            if (isSuperAdmin.value && allTenants.value) {
                return allTenants.value;
            }
            if (!currentUser.value || !currentUser.value.tenants) return [];
            return currentUser.value.tenants.map(t => t.tenant);
        });

        const isAdminForCurrentTenant = computed(() => {
            return hasRightsForChosenTenant(USER_PERMISSIONS.ADMIN, selectedTenantId.value);
        });

        const filteredTenants = computed(() => {
            return tenantQuery.value === ''
                ? currentTenants.value
                : currentTenants.value.filter((tenant) => {
                    return tenant.name.toLowerCase().includes(tenantQuery.value.toLowerCase());
                });
        });

        const filteredDevices = computed(() => {
            if (!newDevice.value) return [];
            const searchTerm = newDevice.value.toLowerCase().trim();
            return tenantDevices.value
                .filter(device => {
                    const serialNumber = device.serialNumber.toLowerCase();
                    return serialNumber.includes(searchTerm) && !currentState.excludedDevices.includes(serialNumber);
                })
                .slice(0, 10);
        });

        onMounted(async () => {
            isLoading.value = true;
            if (allUsers.value.size === 0) await store.dispatch("users/getUsers", true);
            if (isSuperAdmin.value) await store.dispatch("loadAllTenants", false);
            if (currentTenants.value.length > 0) {
                selectedTenantId.value = currentTenants.value[0].id;
                selectedTenant.value = currentTenants.value[0];
            }
            isLoading.value = false;
        });

        watch(selectedTenant, (newTenant) => {
            if (newTenant) {
                selectedTenantId.value = newTenant.id;
            }
        });

        watch(selectedTenantId, async (newId) => {
            if (newId) {
                const tenant = currentTenants.value.find(t => t.id === newId);
                if (tenant) selectedTenant.value = tenant;
                await loadDevicesForTenant(newId);
                fetchSubscriptionData(newId);
            }
        });

        async function loadDevicesForTenant(tenantId) {
            loadingDevices.value = true;
            try {
                const existingTenants = store.state.tenants || [];
                if (!existingTenants.find(t => t.id === tenantId)) {
                    const tenant = currentTenants.value.find(t => t.id === tenantId);
                    if (tenant) {
                        store.commit("setTenants", [...existingTenants, { ...tenant }]);
                    }
                }
                await store.dispatch("loadTenantDevices", tenantId);
            } catch (error) {
                console.error('Failed to load devices for tenant:', error);
            } finally {
                loadingDevices.value = false;
            }
        }

        async function fetchSubscriptionData(tenantId) {
            if (!tenantId) return;
            isSubscriptionLoading.value = true;
            try {
                const response = await querySubscription(tenantId);
                if (response && response.ownerId) {
                    subscriptionExists.value = true;
                    const mappedData = {
                        emails: response.emailList || [],
                        excludedDevices: response.excludedDevices || [],
                        isActive: response.subscriptionStatus === 'ACTIVE'
                    };
                    Object.assign(currentState, mappedData);
                    originalState.value = JSON.parse(JSON.stringify(mappedData));
                } else {
                    subscriptionExists.value = false;
                    const initialData = { emails: [], excludedDevices: [], isActive: false };
                    Object.assign(currentState, initialData);
                    originalState.value = JSON.parse(JSON.stringify(initialData));
                }
            } catch (error) {
                console.error(`Failed to fetch subscription for tenant ${tenantId}:`, error);
            } finally {
                isSubscriptionLoading.value = false;
            }
        }

        // IMPROVED EMAIL LOGIC
        function addEmail() {
            const inputText = newEmail.value.trim();
            if (!inputText) return;

            // Remove trailing delimiters from input
            const cleanInput = inputText.replace(/[ ,;]+$/, '');
            const emails = cleanInput.split(/[\s,;\n]+/).map(e => e.trim()).filter(Boolean);

            let emailAdded = false;
            emails.forEach(email => {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (email && emailRegex.test(email) && !currentState.emails.includes(email)) {
                    currentState.emails.push(email);
                    emailAdded = true;
                }
            });

            if (emailAdded && currentState.emails.length === 1) {
                currentState.isActive = true;
            }
            newEmail.value = '';
        }

        function handleEmailKeyup(event) {
            const delimiters = [',', ';', ' '];
            if (delimiters.includes(event.key)) {
                addEmail();
            }
        }

        function handlePaste(event) {
            const pastedText = event.clipboardData.getData('text');
            const emails = pastedText.split(/[\s,;\n]+/).map(e => e.trim()).filter(Boolean);
            const initialEmailCount = currentState.emails.length;
            emails.forEach(email => {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (email && emailRegex.test(email) && !currentState.emails.includes(email)) {
                    currentState.emails.push(email);
                }
            });
            if (initialEmailCount === 0 && currentState.emails.length > 0) {
                currentState.isActive = true;
            }
        }

        function handleBackspace() {
            if (newEmail.value === '' && currentState.emails.length > 0) {
                currentState.emails.pop();
                if (currentState.emails.length === 0) {
                    currentState.isActive = false;
                }
            }
        }

        function removeEmail(index) {
            currentState.emails.splice(index, 1);
            if (currentState.emails.length === 0) {
                currentState.isActive = false;
            }
        }

        function addDevice() {
            const inputText = newDevice.value.trim();
            if (!inputText) return;
            const deviceIds = inputText.split(/[\s,;\n]+/).map(d => d.trim().toLowerCase()).filter(Boolean);
            const deviceIdRegex = /^[a-z0-9]{8}$/;
            let hasInvalid = false;
            let hasDuplicate = false;
            let hasNotFound = false;
            let anyAdded = false;

            deviceIds.forEach(deviceId => {
                if (!deviceIdRegex.test(deviceId)) {
                    hasInvalid = true;
                    return;
                }
                if (currentState.excludedDevices.includes(deviceId)) {
                    hasDuplicate = true;
                    return;
                }
                const existsInTenant = tenantDevices.value.some(d => d.serialNumber.toLowerCase() === deviceId);
                if (!existsInTenant) {
                    hasNotFound = true;
                    return;
                }
                currentState.excludedDevices.push(deviceId);
                anyAdded = true;
            });

            if (hasInvalid) {
                deviceError.value = t('deviceAlarmsNotificationSubscription.invalidDeviceId');
            } else if (hasNotFound) {
                deviceError.value = t('deviceAlarmsNotificationSubscription.deviceNotFoundInTenant');
            } else if (!anyAdded && hasDuplicate) {
                deviceError.value = t('deviceAlarmsNotificationSubscription.duplicateDeviceId');
            } else {
                deviceError.value = '';
            }

            if (anyAdded || (!hasInvalid && !hasNotFound)) {
                newDevice.value = '';
            }
        }

        function selectDevice(serialNumber) {
            const deviceId = serialNumber.toLowerCase();
            if (!currentState.excludedDevices.includes(deviceId)) {
                currentState.excludedDevices.push(deviceId);
            }
            newDevice.value = '';
            deviceInputFocused.value = false;
            deviceError.value = '';
        }

        function handleDeviceInput() {
            if (deviceError.value) deviceError.value = '';
        }

        function handleDeviceBlur() {
            setTimeout(() => { deviceInputFocused.value = false; }, 200);
        }

        function highlightMatch(text, search) {
            if (!search) return text;
            const searchTerm = search.toLowerCase().trim();
            const lowerText = text.toLowerCase();
            const index = lowerText.indexOf(searchTerm);
            if (index === -1) return text;
            const before = text.substring(0, index);
            const match = text.substring(index, index + searchTerm.length);
            const after = text.substring(index + searchTerm.length);
            return `${before}<span class="bg-yellow-200">${match}</span>${after}`;
        }

        function handleDevicePaste(event) {
            const pastedText = event.clipboardData.getData('text');
            const deviceIds = pastedText.split(/[\s,;\n]+/).map(d => d.trim().toLowerCase()).filter(Boolean);
            const deviceIdRegex = /^[a-z0-9]{8}$/;
            let hasInvalid = false;
            let hasNotFound = false;
            deviceIds.forEach(deviceId => {
                if (!deviceIdRegex.test(deviceId)) {
                    hasInvalid = true;
                    return;
                }
                const existsInTenant = tenantDevices.value.some(d => d.serialNumber.toLowerCase() === deviceId);
                if (!existsInTenant) {
                    hasNotFound = true;
                    return;
                }
                if (!currentState.excludedDevices.includes(deviceId)) {
                    currentState.excludedDevices.push(deviceId);
                }
            });
            if (hasInvalid) {
                deviceError.value = t('deviceAlarmsNotificationSubscription.invalidDeviceId');
            } else if (hasNotFound) {
                deviceError.value = t('deviceAlarmsNotificationSubscription.deviceNotFoundInTenant');
            } else {
                deviceError.value = '';
            }
        }

        function handleDeviceBackspace() {
            if (newDevice.value === '' && currentState.excludedDevices.length > 0) currentState.excludedDevices.pop();
        }

        function removeDevice(index) {
            currentState.excludedDevices.splice(index, 1);
        }

        function closeModal() {
            showConfirmationModal.value = false;
        }

        async function handleConfirmAction() {
            const action = modalActionType.value;
            closeModal();

            if (action === 'create') {
                await submitCreate();
            } else if (action === 'update') {
                await submitUpdate();
            } else if (action === 'delete') {
                await submitDelete();
            } else if (action === 'abort') {
                await submitAbort();
            }
        }

        function handleCreate() {
            modalTitle.value = t('deviceAlarmsNotificationSubscription.popups.create.title');
            modalDescription.value = t('deviceAlarmsNotificationSubscription.popups.create.description');
            modalActionText.value = t('deviceAlarmsNotificationSubscription.popups.create.actionText');
            modalActionType.value = 'create';
            modalType.value = 'info';
            showConfirmationModal.value = true;
        }

        function handleUpdate() {
            modalTitle.value = t('deviceAlarmsNotificationSubscription.popups.update.title');
            modalDescription.value = t('deviceAlarmsNotificationSubscription.popups.update.description');
            modalActionText.value = t('deviceAlarmsNotificationSubscription.popups.update.actionText');
            modalActionType.value = 'update';
            modalType.value = 'info';
            showConfirmationModal.value = true;
        }

        function handleDelete() {
            if (!subscriptionExists.value) return;
            modalTitle.value = t('deviceAlarmsNotificationSubscription.popups.delete.title');
            modalDescription.value = t('deviceAlarmsNotificationSubscription.popups.delete.description');
            modalActionText.value = t('deviceAlarmsNotificationSubscription.popups.delete.actionText');
            modalType.value = 'danger';
            modalActionType.value = 'delete';
            showConfirmationModal.value = true;
        }

        function handleAbort() {
            modalTitle.value = t('deviceAlarmsNotificationSubscription.popups.abort.title');
            modalDescription.value = t('deviceAlarmsNotificationSubscription.popups.abort.description');
            modalActionText.value = t('deviceAlarmsNotificationSubscription.popups.abort.actionText');
            modalType.value = 'danger';
            modalActionType.value = 'abort';
            showConfirmationModal.value = true;
        }

        async function submitCreate() {
            const subscriptionStatus = currentState.isActive ? 'ACTIVE' : 'INACTIVE';
            const newSubData = {
                ownerId: selectedTenantId.value,
                emailList: currentState.emails || [],
                excludedDevices: currentState.excludedDevices || [],
                subscriptionStatus: subscriptionStatus
            };
            await createSubscription(newSubData)
            await fetchSubscriptionData(selectedTenantId.value);
        }

        async function submitUpdate() {
            const subscriptionStatus = currentState.isActive ? 'ACTIVE' : 'INACTIVE';
            const updatedData = {
                ownerId: selectedTenantId.value,
                emailList: currentState.emails || [],
                excludedDevices: currentState.excludedDevices || [],
                subscriptionStatus: subscriptionStatus
            };
            await updateSubscription(updatedData)
            originalState.value = JSON.parse(JSON.stringify(currentState));
        }

        async function submitDelete() {
            await deleteSubscription(selectedTenantId.value)
            await fetchSubscriptionData(selectedTenantId.value)
        }

        async function submitAbort() {
            await fetchSubscriptionData(selectedTenantId.value)
        }

        return {
            isLoading,
            isSubscriptionLoading,
            subscriptionExists,
            currentState,
            newEmail,
            newDevice,
            deviceInputFocused,
            filteredDevices,
            loadingDevices,
            deviceError,
            currentTenants,
            selectedTenantId,
            selectedTenant,
            tenantQuery,
            filteredTenants,
            isSaveDisabled,
            isCreateDisabled,
            isAbortDisabled,
            addEmail,
            handleEmailKeyup,
            handlePaste,
            handleBackspace,
            removeEmail,
            addDevice,
            selectDevice,
            handleDeviceInput,
            handleDeviceBlur,
            highlightMatch,
            handleDevicePaste,
            handleDeviceBackspace,
            removeDevice,
            handleCreate,
            handleUpdate,
            handleDelete,
            handleAbort,
            showConfirmationModal,
            modalTitle,
            modalDescription,
            modalActionText,
            modalType,
            closeModal,
            handleConfirmAction,
            isSuperAdmin,
            hasRightsForCurrentlyChosenTenant: hasRightsForChosenTenant,
            isAdminForCurrentTenant
        };
    }
};
</script>
